import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useRef, useState } from "react";

export default function SobreNos() {
  const valoresRef = useRef<HTMLDivElement>(null);
  const missaoRef = useRef<HTMLDivElement>(null);
  const visaoRef = useRef<HTMLDivElement>(null);

  const [isValoresVisible, setIsValoresVisible] = useState(false);
  const [isMissaoVisible, setIsMissaoVisible] = useState(false);
  const [isVisaoVisible, setIsVisaoVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === valoresRef.current) {
              setIsValoresVisible(true);
            } else if (entry.target === missaoRef.current) {
              setIsMissaoVisible(true);
            } else if (entry.target === visaoRef.current) {
              setIsVisaoVisible(true);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    if (valoresRef.current) observer.observe(valoresRef.current);
    if (missaoRef.current) observer.observe(missaoRef.current);
    if (visaoRef.current) observer.observe(visaoRef.current);

    return () => {
      if (valoresRef.current) observer.unobserve(valoresRef.current);
      if (missaoRef.current) observer.unobserve(missaoRef.current);
      if (visaoRef.current) observer.unobserve(visaoRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero */}
        <section className="relative text-primary-foreground h-[265px] flex items-center" style={{backgroundImage: "url('/images/pages/hero-laranja.webp')", backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: "0 0 96px 0", overflow: "hidden", boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)"}}>
          <div className="absolute inset-0" style={{background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 40%, rgba(0, 0, 0, 0.2) 70%, rgba(0, 0, 0, 0) 100%)"}}></div>
          <div className="container relative z-10">
            <h1 className="text-4xl md:text-5xl text-center">
              Sobre Nós
            </h1>
          </div>
        </section>

        {/* Quem somos nós */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Imagem da equipe */}
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img loading="lazy" src="/images/pages/sobre-nos-equipe.webp" alt="Equipe Fercci" className="w-full h-full object-cover" />
              </div>
              
              {/* Texto */}
              <div>
                <h2 className="text-3xl md:text-4xl mb-6 text-foreground">
                  Quem somos nós?
                </h2>
                
                <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    A <strong className="text-foreground">Fercci</strong> é sinônimo de <strong>tradição e inovação</strong> no agronegócio brasileiro. Como representantes oficiais da <strong className="text-foreground">Yara</strong>, somos referência em soluções fertilizantes para o Cerrado, <strong className="text-foreground">impulsionando a produtividade e a rentabilidade</strong> dos nossos clientes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pioneiros e especialistas */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl mb-8 text-foreground">
                  Pioneiros e especialistas
                </h2>
                
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed max-w-4xl">
                  <p>
                    Nossa trajetória é marcada por <strong className="text-foreground">pioneirismo</strong> e busca constante por conhecimento. Somos pioneiros em eventos com temática financeira agrícola, oferecendo aos nossos clientes ferramentas e informações essenciais para a tomada de decisões estratégicas.
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img loading="lazy" src="/images/Pioneiros.jpg" alt="Pioneiros" className="w-full h-auto object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Reconhecimento e excelência */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img loading="lazy" src="/images/Reconhecimento.jpg" alt="Reconhecimento" className="w-full h-auto object-cover" />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl mb-8 text-foreground">
                  Reconhecimento e excelência
                </h2>
                
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed max-w-4xl">
                  <p>
                    Ao longo dos anos, conquistamos o respeito e a confiança dos nossos clientes e parceiros. Somos destaque no programa de representantes da Yara, alcançando a categoria <strong className="text-foreground">diamante</strong> no programa Boa Colheita por diversos anos consecutivos. Esse reconhecimento é fruto do nosso compromisso com a excelência e da nossa busca contínua por soluções inovadoras para a agricultura.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Empresa familiar */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl mb-8 text-foreground">
                  Uma empresa familiar com olhar para o futuro
                </h2>
                
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed max-w-4xl">
                  <p>
                    A Fercci é uma empresa familiar que une a tradição da agricultura com a modernidade da nova geração. Acreditamos que a agricultura é a base para um futuro mais sustentável e próspero. Por isso, investimos em tecnologias e soluções que contribuem para a otimização dos recursos naturais e a preservação do meio ambiente.
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img loading="lazy" src="/images/EmpresaFamiliar.jpg" alt="Empresa Familiar" className="w-full h-auto object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Missão, Visão e Valores */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container">
            <h2 className="text-3xl md:text-4xl mb-12 text-foreground text-center">
              Missão, Visão e Valores
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Valores - Seiva (Verde) - Esquerda, 2 linhas - Slide in esquerda + fade in */}
              <div
                ref={valoresRef}
                className="lg:row-span-2"
                style={{
                  transform: isValoresVisible ? 'translateX(0)' : 'translateX(-100px)',
                  opacity: isValoresVisible ? 1 : 0,
                  transition: 'all 0.8s ease-out'
                }}
              >
                <Card className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30  hover:shadow-lg transition-all" style={{backgroundColor: 'rgba(29, 134, 73, 0.25)', border: '2px solid #1D8649', borderRadius: '3rem 0.5rem 0.5rem 0.5rem'}}>
                  <CardContent className="p-8 h-full flex flex-col justify-center">
                    <h3 className="titulo-card mb-6" style={{color: '#1d8649'}}>
                      Nossos Valores
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <p className="font-semibold" style={{color: '#1d8649'}}>Tradição:</p>
                        <p className="text-sm text-muted-foreground">Preservamos a experiência e o conhecimento adquiridos ao longo de mais de 30 anos de atuação no mercado.</p>
                      </div>
                      <div>
                        <p className="font-semibold" style={{color: '#1d8649'}}>Inovação:</p>
                        <p className="text-sm text-muted-foreground">Buscamos constantemente novas tecnologias e soluções para atender às necessidades dos nossos clientes.</p>
                      </div>
                      <div>
                        <p className="font-semibold" style={{color: '#1d8649'}}>Parceria:</p>
                        <p className="text-sm text-muted-foreground">Construímos relações duradouras com nossos clientes, fornecedores e parceiros.</p>
                      </div>
                      <div>
                        <p className="font-semibold" style={{color: '#1d8649'}}>Excelência:</p>
                        <p className="text-sm text-muted-foreground">Oferecemos produtos e serviços de alta qualidade, com foco na satisfação do cliente.</p>
                      </div>
                      <div>
                        <p className="font-semibold" style={{color: '#1d8649'}}>Sustentabilidade:</p>
                        <p className="text-sm text-muted-foreground">Acreditamos em uma agricultura sustentável, que respeita o meio ambiente e as futuras gerações.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Direita - Grid com Missão e Visão */}
              <div className="flex flex-col gap-8">
                {/* Missão - Sereno (Azul) - Slide in direita + fade in */}
                <div
                  ref={missaoRef}
                  style={{
                    transform: isMissaoVisible ? 'translateX(0)' : 'translateX(100px)',
                    opacity: isMissaoVisible ? 1 : 0,
                    transition: 'all 0.8s ease-out'
                  }}
                >
                  <Card className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30  hover:shadow-lg transition-all" style={{backgroundColor: 'rgba(0, 51, 102, 0.25)', border: '2px solid #003366', borderRadius: '0.5rem 0.5rem 3rem 0.5rem'}}>
                    <CardContent className="p-8 h-full flex flex-col justify-center">
                      <h3 className="titulo-card mb-4" style={{color: '#003366'}}>
                        Nossa Missão
                      </h3>
                      <p className="leading-relaxed">
                        Oferecer soluções completas em fertilizantes, combinadas com serviços de alta qualidade, para que nossos clientes alcancem o máximo potencial produtivo de suas lavouras, garantindo a sustentabilidade dos seus negócios.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Visão - Aurora (Amarelo) - Slide in baixo + fade in */}
                <div
                  ref={visaoRef}
                  style={{
                    transform: isVisaoVisible ? 'translateY(0)' : 'translateY(100px)',
                    opacity: isVisaoVisible ? 1 : 0,
                    transition: 'all 0.8s ease-out'
                  }}
                >
                  <Card className="h-full w-full bg-clip-padding backdrop-filter backdrop-blur-md bg-opacity-30  hover:shadow-lg transition-all" style={{backgroundColor: 'rgba(211, 214, 85, 0.25)', border: '2px solid #D3D655', borderRadius: '0.5rem 0.5rem 3rem 0.5rem'}}>
                    <CardContent className="p-8 h-full flex flex-col justify-center">
                      <h3 className="titulo-card mb-4" style={{color: '#003366'}}>
                        Nossa Visão
                      </h3>
                      <p className="leading-relaxed">
                        Sermos reconhecidos como a principal referência em soluções agrícolas para o Cerrado, impulsionando os resultados, a produtividade e a sustentabilidade dos nossos clientes, através de parcerias duradouras e inovação contínua.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

